import { useEffect, useLayoutEffect, useRef } from 'react';
import { ensureFontsForElements, resolveFileUrl } from './fonts';
import { SHAPES, lineDash } from './shapes';
import { runsOf, domToRuns } from './richText';

export const elementStyle = (el, s) => ({
  position: 'absolute',
  left: el.x * s,
  top: el.y * s,
  width: el.w * s,
  height: el.h * s,
  transform: `rotate(${el.rotation || 0}deg)`,
  opacity: el.opacity ?? 1,
  display: el.hidden ? 'none' : undefined,
});

const escapeHtml = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const runsToHtml = (runs) =>
  runs.map((r) => (r.color ? `<span style="color:${r.color}">${escapeHtml(r.text)}</span>` : escapeHtml(r.text))).join('');

export const ElementContent = ({ el, scale: s, editing, onTextCommit }) => {
  const textRef = useRef(null);
  useLayoutEffect(() => {
    if (editing && textRef.current) {
      const node = textRef.current;
      node.innerHTML = runsToHtml(runsOf(el));
      node.focus();
      const range = document.createRange();
      range.selectNodeContents(node);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [editing]);
  if (el.type === 'text') {
    const runs = runsOf(el);
    return (
      <div
        key={editing ? 'edit' : 'view'}
        ref={textRef}
        data-rich-text={editing ? 'true' : undefined}
        contentEditable={editing || undefined}
        suppressContentEditableWarning
        onBlur={editing ? (e) => { if (e.relatedTarget?.closest?.('[data-rich-toolbar]')) return; onTextCommit?.(domToRuns(e.currentTarget)); } : undefined}
        onKeyDown={editing ? (e) => { if (e.key === 'Escape') e.currentTarget.blur(); } : undefined}
        style={{
          width: '100%',
          height: '100%',
          fontFamily: `'${el.fontFamily || 'Manrope'}', sans-serif`,
          fontSize: (el.fontSize || 24) * s,
          fontWeight: el.fontWeight || 400,
          fontStyle: el.fontStyle || 'normal',
          color: el.color || '#ffffff',
          textAlign: el.align || 'left',
          lineHeight: el.lineHeight || 1.2,
          letterSpacing: (el.letterSpacing || 0) * s,
          textTransform: el.uppercase ? 'uppercase' : 'none',
          textShadow: el.shadow ? `0 ${2 * s}px ${8 * s}px rgba(0,0,0,0.5)` : 'none',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflow: 'visible',
          outline: editing ? `${Math.max(1, s)}px dashed #EAB308` : 'none',
          cursor: editing ? 'text' : undefined,
          userSelect: editing ? 'text' : 'none',
        }}
      >
        {editing ? null : runs.map((r, i) => (r.color ? <span key={i} style={{ color: r.color }}>{r.text}</span> : <span key={i}>{r.text}</span>))}
      </div>
    );
  }
  if (el.type === 'image') {
    return el.src ? (
      <img
        src={resolveFileUrl(el.src)}
        alt=""
        draggable={false}
        style={{ width: '100%', height: '100%', objectFit: el.fit || 'cover', borderRadius: (el.radius || 0) * s, display: 'block', pointerEvents: 'none' }}
      />
    ) : (
      <div style={{ width: '100%', height: '100%', borderRadius: (el.radius || 0) * s, background: 'repeating-linear-gradient(45deg,#27272a 0 8px,#18181b 8px 16px)' }} />
    );
  }
  if (el.type === 'line') {
    const sw = (el.strokeWidth || 3) * s;
    const dash = lineDash(el.dash, sw);
    return (
      <svg width="100%" height="100%" style={{ display: 'block', overflow: 'visible' }}>
        <line
          x1={0}
          y1="50%"
          x2="100%"
          y2="50%"
          stroke={el.stroke || '#ffffff'}
          strokeWidth={sw}
          strokeLinecap={el.dash === 'dotted' ? 'round' : el.cap || 'round'}
          strokeDasharray={dash.length ? dash.join(' ') : undefined}
        />
      </svg>
    );
  }
  if (el.type === 'shape') {
    const def = SHAPES[el.shape] || SHAPES.triangle;
    const sw = (el.strokeWidth || 0) * s;
    return (
      <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ display: 'block', overflow: 'visible' }}>
        <path
          d={def.path}
          fill={el.fill || '#A855F7'}
          fillRule={def.evenOdd ? 'evenodd' : 'nonzero'}
          stroke={sw ? el.stroke || '#ffffff' : 'none'}
          strokeWidth={sw}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    );
  }
  const shape = {
    width: '100%',
    height: '100%',
    background: el.fill || '#A855F7',
    border: el.strokeWidth ? `${el.strokeWidth * s}px solid ${el.stroke || '#ffffff'}` : 'none',
    borderRadius: el.type === 'ellipse' ? '50%' : (el.radius || 0) * s,
    boxSizing: 'border-box',
  };
  return <div style={shape} />;
};

/** Read-only render of a menu at a given pixel width (thumbnails + viewer). */
export const MenuCanvas = ({ menu, width, customFonts = [], elements, className = '', style = {} }) => {
  const canvas = menu.canvas || { width: 390, height: 844, background: '#09090b' };
  const s = width / canvas.width;
  const els = elements || menu.elements || [];
  useEffect(() => { ensureFontsForElements(els, customFonts); }, [els, customFonts]);

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={{ width, height: canvas.height * s, background: canvas.background || '#09090b', ...style }}
      data-testid="menu-canvas"
    >
      {els.filter((e) => !e.hidden).map((el) => (
        <div key={el.id} style={elementStyle(el, s)}>
          <ElementContent el={el} scale={s} />
        </div>
      ))}
    </div>
  );
};
